import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { ContactCreateDialog } from "@/app/Contacts/Components/ContactCreateDialog";
import {
  BaseComboboxInputProps,
  ComboboxInput,
} from "@/lib/Components/Form/Inputs/ComboboxInput";
import {
  ContactListItem,
  contactListQuery,
} from "@/app/Contacts/GraphQL/contactListQuery";
import { CreateContactInput } from "@/gql/graphql";
import { ResultOf, VariablesOf } from "@graphql-typed-document-node/core";
import { contactRecordQuery } from "@/app/Contacts/GraphQL/contactRecordQuery";
import { Link } from "@/components/catalyst/link";
import { useTranslations } from "use-intl";

export function ContactInput({
  initialFixtureValues,
  organisationId,
  ...props
}: BaseComboboxInputProps<
  ResultOf<typeof contactListQuery>,
  VariablesOf<typeof contactListQuery>,
  ContactListItem,
  ResultOf<typeof contactRecordQuery>,
  VariablesOf<typeof contactRecordQuery>
> & {
  initialFixtureValues?: Partial<CreateContactInput>;
  organisationId?: string;
}) {
  const { openAsPromise } = useDialog(ContactCreateDialog);

  return (
    <ComboboxInput
      document={contactListQuery}
      accessor={(data) => data.contacts.data}
      getQueryVariables={(search) => ({
        first: 20,
        page: 1,
        managedById: organisationId,
        search,
      })}
      recordDocument={contactRecordQuery}
      recordAccessor={(data) => data.contact}
      getViewNode={(i) =>
        i ? (
          <Link
            from="/$tenant/$orgId/supplier/{-$supplierId}"
            to="/$tenant/$orgId/supplier/{-$supplierId}/contacts/$id"
            className="text-blue-500 underline"
            params={(prev) => ({
              ...prev,
              id: i.id,
            })}
          >
            {i.name}
          </Link>
        ) : (
          "--"
        )
      }
      getKey={(item) => item.id}
      getListItemNode={(item) => <Node model={item} />}
      getInputNode={(item) => item.name}
      fixture={(search) => ({
        label: `Add ${search}`,
        onClick() {
          return openAsPromise({
            initialValues: {
              name: search,
              ...initialFixtureValues,
            },
          });
        },
      })}
      {...props}
    />
  );
}

function Node({ model }: { model: ContactListItem }) {
  const t = useTranslations("contact");

  return (
    <div className="flex items-center space-x-3">
      <div className="grow whitespace-nowrap">
        <p className="">{model.name}</p>
        <p className="text-xs text-gray-500">{t(`type.${model.type}`)}</p>
      </div>
    </div>
  );
}
